/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { v4 as uuidv4 } from 'uuid';
import { renderToStaticMarkup } from '@web-stories-wp/react';
import { PAGE_HEIGHT, PAGE_WIDTH } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { getDefinitionForType } from '../elements';
import generateGroupName from './generateGroupName';
import { remapTextBoxAttachment } from './textBoxAttachment';

const DOUBLE_DASH_ESCAPE = '_DOUBLEDASH_';
const STORY_ELEMENTS_ATTRIBUTE = 'data-storyteller-elements';

/**
 * Processes pasted content to find story elements.
 *
 * @param {DocumentFragment} content NodeList representation of the content.
 * @param {Object}           currentPage Current page.
 * @param {boolean}          persistPositionAndNames Whether to maintain position of pasted elements.
 * @return {Object} Object containing found elements and animations arrays.
 */
export function processPastedElements(
  content,
  currentPage,
  persistPositionAndNames = false
) {
  let foundElementsAndAnimations = { animations: [], elements: [] };
  const encodedPayload = content
    .querySelector?.(`[${STORY_ELEMENTS_ATTRIBUTE}]`)
    ?.getAttribute(STORY_ELEMENTS_ATTRIBUTE);
  if (encodedPayload) {
    try {
      const serializedPayload = decodeURIComponent(encodedPayload);
      const payload = JSON.parse(serializedPayload);
      if (payload?.sentinel === 'story-elements') {
        content.insertBefore(
          document.createComment(
            serializedPayload.replace(/--/g, DOUBLE_DASH_ESCAPE)
          ),
          content.firstChild
        );
      }
    } catch {
      // Ignore malformed data attributes and try the legacy HTML comment.
    }
  }
  for (let n = content.firstChild; n; n = n.nextSibling) {
    if (
      n.nodeType !== /* COMMENT */ 8 ||
      n.nodeValue?.indexOf('Fragment') !== -1
    ) {
      continue;
    }
    const payload = JSON.parse(
      n.nodeValue.replace(new RegExp(DOUBLE_DASH_ESCAPE, 'g'), '--')
    );
    if (payload.sentinel !== 'story-elements') {
      continue;
    }

    const elementIdTransferMap = Object.fromEntries(
      payload.items
        .map((item) => [item.basedOn || item.id, uuidv4()])
        .filter(([id]) => id)
    );
    const processedPayload = payload.items.reduce(
      (accum, { x, y, basedOn, ...rest }) => {
        const originalId = basedOn || rest.id;
        const elementId = elementIdTransferMap[originalId] || uuidv4();
        const elementAnimations = payload.animations
          .filter((animation) => animation.targets.includes(originalId))
          .map((animation) => ({
            ...animation,
            targets: [elementId],
            id: uuidv4(),
          }));

        currentPage.elements.forEach((element) => {
          if (
            !persistPositionAndNames &&
            (element.id === originalId || element.basedOn === originalId)
          ) {
            const pastedXY = getPastedCoordinates(x, y);
            x = pastedXY.x;
            y = pastedXY.y;
          }
        });

        const elementsWithSameGroupId = rest.groupId
          ? currentPage.elements
              .filter((pageEl) => pageEl.groupId === rest.groupId)
              .map((pageEl) => pageEl.id)
          : [];

        const copiedElementsWithSameGroupId = payload.items
          .filter((item) => item.groupId === rest.groupId)
          .map((item) => item.id || item.basedOn);

        const pastedElement = remapTextBoxAttachment(
          {
            ...rest,
            basedOn,
            id: elementId,
            x,
            y,
            ...(elementsWithSameGroupId.length > 0
              ? {
                  shouldCopyWholeGroup: elementsWithSameGroupId.every(
                    (elementId) =>
                      copiedElementsWithSameGroupId.includes(elementId)
                  ),
                }
              : {}),
          },
          elementIdTransferMap
        );

        return {
          elements: [...accum.elements, pastedElement],
          animations: [...accum.animations, ...elementAnimations],
        };
      },
      { animations: [], elements: [] }
    );

    const groups = {
      ...payload.groups,
    };

    for (const groupId of Object.keys(groups)) {
      const copiedElementInGroup = processedPayload.elements.find(
        (element) => element.groupId === groupId
      );

      const shouldCopyWholeGroup =
        copiedElementInGroup?.shouldCopyWholeGroup ?? true;

      if (shouldCopyWholeGroup) {
        groups[groupId].name = generateGroupName(
          groups,
          groups[groupId].name,
          persistPositionAndNames
        );
      } else {
        delete groups[groupId];
      }
    }

    foundElementsAndAnimations = {
      animations: [
        ...foundElementsAndAnimations.animations,
        ...processedPayload.animations,
      ],
      elements: [
        ...foundElementsAndAnimations.elements,
        ...processedPayload.elements,
      ],
      groups,
    };

    return foundElementsAndAnimations;
  }
  return foundElementsAndAnimations;
}

export function formatHtmlContent(payload, htmlContent) {
  const rawPayload = JSON.stringify(payload);
  const serializedPayload = rawPayload.replace(/--/g, DOUBLE_DASH_ESCAPE);

  return `<span ${STORY_ELEMENTS_ATTRIBUTE}="${encodeURIComponent(
    rawPayload
  )}"></span><!-- ${serializedPayload} -->${htmlContent}`;
}

/**
 * Processes copied/cut content for preparing elements to add to clipboard.
 *
 * @param {Object} page Page which all the elements belong to.
 * @param {Array} elements Array of story elements.
 * @param {Array} animations Array of story animations.
 * @param {Array} groups Array of story groups.
 * @param {Object} evt Copy/cut event object.
 */
export function addElementsToClipboard(
  page,
  elements,
  animations,
  groups,
  evt
) {
  if (!elements.length || !evt) {
    return;
  }
  const { clipboardData } = evt;
  const payload = {
    sentinel: 'story-elements',
    // @todo: Ensure that there's no unserializable data here. The easiest
    // would be to keep all serializable data together and all non-serializable
    // in a separate property.
    items: elements.map((element) => ({
      ...element,
      ...(element.isDefaultBackground
        ? { backgroundColor: page.backgroundColor }
        : null),
      basedOn: element.id,
      id: undefined,
    })),
    animations,
    groups,
  };

  const textContent = elements
    .map((el) => {
      const { type } = el;
      const { TextContent } = getDefinitionForType(type);
      if (TextContent) {
        return TextContent(el);
      }
      return type;
    })
    .join('\n');

  const htmlContent = elements
    .map((el) => {
      const { type, x, y, rotationAngle } = el;
      const { Output } = getDefinitionForType(type);
      return renderToStaticMarkup(
        <Output
          element={el}
          box={{ width: 100, height: 100, x, y, rotationAngle }}
        />
      );
    })
    .join('\n');

  clipboardData.setData('text/plain', textContent);
  clipboardData.setData('text/html', formatHtmlContent(payload, htmlContent));
}

/**
 * Gets x, y values for cloned/pasted element, ensuring it's not added out of the page.
 *
 * @param {number} originX Original X.
 * @param {number} originY Original Y.
 * @return {{x: (number), y: (number)}} Coordinates.
 */
export function getPastedCoordinates(originX, originY) {
  const placementDiff = 30;
  const allowedBorderDistance = 20;
  const x = originX + placementDiff;
  const y = originY + placementDiff;
  return {
    x: PAGE_WIDTH - x > allowedBorderDistance ? x : placementDiff,
    y: PAGE_HEIGHT - y > allowedBorderDistance ? y : placementDiff,
  };
}
